import type { JSX } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import { component$, useSignal, useVisibleTask$ } from '@builder.io/qwik';
import { Logger } from '~/utilities/logger';
import { useBlockClasses } from '~/utilities/useBlockClasses';

const cssClasses = cva('my-[18px] [&>iframe]:max-w-full flex justify-center', {
  variants: {
    type: {
      iframe: '',
      other: '',
      youtube:
        'relative aspect-video overflow-hidden max-w-full bg-black [&>iframe]:absolute [&>iframe]:top-0 [&>iframe]:left-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0',
      vimeo:
        'relative aspect-video overflow-hidden max-w-full bg-black [&>iframe]:absolute [&>iframe]:top-0 [&>iframe]:left-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0',
      instagram: '',
      facebook: '',
      riddle: 'pt-5 px-5 bg-lightGrey relative max-w-full',
    },
  },
});

export type EmbedType = {
  embed: string;
  type:
    | 'iframe'
    | 'youtube'
    | 'vimeo'
    | 'instagram'
    | 'facebook'
    | 'riddle'
    | 'other';
  style?: Record<string, string>;
  loadScript?: string;
  isLoadedProp?: 'mmInstagramLoaded' | 'mmFacebookLoaded' | 'mmRiddleLoaded';
  height?: string;
};

export default component$<EmbedType>(
  ({ embed, type, style = {}, loadScript, isLoadedProp }): JSX.Element => {
    const embedHTML = useSignal('');
    const blockClasses = useBlockClasses();

    // This uses an intersection observer on the client side to update the signal and load the iframe
    // when it is scrolled into view
    // Throws a linter warning because it can be misused, but this is a good use case for it
    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      if (loadScript) {
        if (isLoadedProp) {
          if (window[isLoadedProp]) {
            return;
          }
          window[isLoadedProp] = true;
        }

        const script = document.createElement('script');
        script.src = loadScript;
        script.async = true;
        document.head.appendChild(script);
      }
      embedHTML.value = embed;
    });

    if (!embedHTML.value) {
      if (!embed) {
        new Logger().error('Embed HTML not found');
      }
      return <></>;
    }

    return (
      <figure
        style={style}
        class={cssClasses({ type, class: blockClasses })}
        dangerouslySetInnerHTML={embedHTML.value}
      />
    );
  }
);
